import React from 'react';
import { useForm } from '../../context/FormContext';
import StepLayout from '../StepLayout';
import SelectionCard from '../SelectionCard';

function Step6TechnicalRequirements() {
  const { formData, updateFormData } = useForm();
  const stepData = formData.step6;

  const handleInputChange = (field, value) => {
    updateFormData('step6', { [field]: value });
  };

  const handleSocialMediaChange = (platform, value) => {
    const currentSocialMedia = stepData.socialMedia || {};
    updateFormData('step6', { socialMedia: { ...currentSocialMedia, [platform]: value } });
  };

  const domainOptions = [
    { id: 'Yes', title: 'Yes', description: 'I already own a domain' },
    { id: 'No', title: 'No', description: 'Help me register a domain name' }
  ];

  const hostingOptions = [
    { id: 'Primehost', title: 'With Primehost (Recommended)', description: 'Fully managed and optimised for your website' },
    { id: 'Existing', title: 'I already have my own hosting', description: 'You can provide your hosting details separately' },
    { id: 'Unsure', title: 'I am not sure yet', description: 'We can discuss available hosting solutions' }
  ];

  const emailOptions = [
    { id: 'Yes', title: 'Yes, I need professional email addresses', description: '' },
    { id: 'Existing', title: 'I already have business email set up', description: '' }
  ];

  return (
    <StepLayout
      title="Let's get everything connected! 🔗"
      stepNumber={6}
      totalSteps={7}
      nextButtonText="Let's wrap it up"
      nextStep={7}
      prevStep={5}
    >
      <div className="p-4 bg-[#FEF7EC] border-l-4 border-[#F69D0D] rounded-lg mb-8">
        <p className="text-[#92400D] text-base">
          Don't let the word 'technical' worry you – we have made this as straightforward as possible. We will handle all the complex bits while you simply let us know your preferences.
        </p>
      </div>

      <div className="space-y-8">
        {/* Domain Name */}
        <div>
          <h3 className="text-xl font-medium text-gray-900 mb-4">Domain name</h3>
          <p className="text-gray-600 mb-6">Do you currently own a domain name?</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {domainOptions.map((option) => (
              <SelectionCard
                key={option.id}
                title={option.title}
                description={option.description}
                selected={stepData.domainOwnership === option.id}
                onClick={() => handleInputChange('domainOwnership', option.id)}
              />
            ))}
          </div>

          {/* Domain Input Fields */}
          {stepData.domainOwnership === 'Yes' && (
            <div>
              <label className="block text-base font-medium text-gray-900 mb-2">
                Current domain name
              </label>
              <input
                type="text"
                value={stepData.currentDomain || ''}
                onChange={(e) => handleInputChange('currentDomain', e.target.value)}
                placeholder="yourdomain.co.za"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent"
              />
            </div>
          )}

          {stepData.domainOwnership === 'No' && (
            <div className="space-y-8">
              <div>
                <label className="block text-17px font-medium text-gray-900 mb-2 mt-8">
                  Preferred domain name
                </label>
                <input
                  type="text"
                  value={stepData.preferredDomain || ''}
                  onChange={(e) => handleInputChange('preferredDomain', e.target.value)}
                  placeholder="yourbusiness.co.za"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent"
                />
                <p className="text-sm text-gray-500 mt-1">
                  We will check availability and help register your domain
                </p>
              </div>
              <div className="p-4 bg-[#FEF7EC] border-l-4 border-[#F69D0D] rounded-lg">
                <h4 className="font-medium text-base text-[#92400D] mb-2">Subdomain option</h4>
                <p className="text-[#92400D] text-base">
                  As an alternative, we can set you up with a subdomain like mycompany.primenet.co.za
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Website Hosting */}
        <div>
          <h3 className="text-xl font-medium text-gray-900 mb-4 mt-12">Website hosting</h3>
          <p className="text-gray-600 mb-6">Where would you like to host your website?</p>
          <div className="p-4 bg-[#E5F9F9] border-l-4 border-[#008080] rounded-lg mb-6">
            <h4 className="font-medium text-base text-[#008080] mb-2">🚀 Primehost Managed Hosting</h4>
            <p className="text-[#008080] text-base">
              Our managed hosting offers optimal performance, advanced security, and dedicated support. It includes an SSL certificate, daily backups, and a 99.9% uptime guarantee. Migration assistance is provided if you are upgrading from another hosting provider.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
  {hostingOptions.map((option) => (
    <SelectionCard
      key={option.id}
      title={option.title}
      description={option.description}
      selected={stepData.hostingChoice === option.id}
      onClick={() => handleInputChange('hostingChoice', option.id)}
    />
  ))}
</div>
        </div>

        {/* Professional Email Hosting */}
        <div>
          <h3 className="text-xl font-medium text-gray-900 mb-4 mt-12">Professional email hosting</h3>
          <p className="text-gray-600 mb-6 text-base">Do you require professional email addresses for your business?</p>
          <div className="p-4 bg-[#F9FAFB] border-l-4 border-[#FF5841] rounded-lg mb-6">
            <h4 className="font-medium text-gray-900 text-base mb-2">📧 Professional email (e.g. info@yourbusiness.co.za)</h4>
            <p className="text-gray-700 text-base">
              We can set up custom email accounts that match your domain to strengthen your brand's credibility and trust, provides spam protection and secure encryption.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-3">
            {emailOptions.map((option) => (
              <SelectionCard
                key={option.id}
                title={option.title}
                description={option.description}
                selected={stepData.emailHosting === option.id}
                onClick={() => handleInputChange('emailHosting', option.id)}
              />
            ))}
          </div>
        </div>

        {/* Social Media Links */}
        <div>
          <h3 className="text-xl font-medium text-gray-900 mt-12 mb-4">Social media links</h3>
          <p className="text-gray-600 mb-6">
            Provide links to the social media profiles you would like included on your website:
          </p>
          <div className="space-y-8">
            <div>
              <label className="block text-16px font-medium text-gray-900 mb-2">Facebook</label>
              <input
                type="url"
                value={stepData.socialMedia?.facebook || ''}
                onChange={(e) => handleSocialMediaChange('facebook', e.target.value)}
                placeholder="https://facebook.com/yourpage"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-16px font-medium text-gray-900 mb-2">Instagram</label>
              <input
                type="url"
                value={stepData.socialMedia?.instagram || ''}
                onChange={(e) => handleSocialMediaChange('instagram', e.target.value)}
                placeholder="https://instagram.com/yourusername"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-16px font-medium text-gray-900 mb-2">Twitter/X</label>
              <input
                type="url"
                value={stepData.socialMedia?.twitter || ''}
                onChange={(e) => handleSocialMediaChange('twitter', e.target.value)}
                placeholder="https://x.com/yourusername"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-16px font-medium text-gray-900 mb-2">LinkedIn</label>
              <input
                type="url"
                value={stepData.socialMedia?.linkedin || ''}
                onChange={(e) => handleSocialMediaChange('linkedin', e.target.value)}
                placeholder="https://linkedin.com/company/yourcompany"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-16px font-medium text-gray-900 mb-2">YouTube</label>
              <input
                type="url"
                value={stepData.socialMedia?.youtube || ''}
                onChange={(e) => handleSocialMediaChange('youtube', e.target.value)}
                placeholder="https://youtube.com/c/yourchannel"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-16px font-medium text-gray-900 mb-2">TikTok</label>
              <input
                type="url"
                value={stepData.socialMedia?.tiktok || ''}
                onChange={(e) => handleSocialMediaChange('tiktok', e.target.value)}
                placeholder="https://tiktok.com/@yourusername"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-16px font-medium text-gray-900 mb-2">WhatsApp Business</label>
              <input
                type="url"
                value={stepData.socialMedia?.whatsapp || ''}
                onChange={(e) => handleSocialMediaChange('whatsapp', e.target.value)}
                placeholder="https://wa.me/27xxxxxxxxx"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-16px font-medium text-gray-900 mb-2">Other Social Media Links</label>
              <textarea
                value={stepData.socialMedia?.other || ''}
                onChange={(e) => handleSocialMediaChange('other', e.target.value)}
                placeholder="List any additional profiles or platforms not mentioned above."
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent resize-none"
              />
            </div>
          </div>
        </div>
      </div>
    </StepLayout>
  );
}

export default Step6TechnicalRequirements;